import {
  MAX_QUERY_LENGTH,
  MIN_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS
} from '../config'
import {
  AutocompleteRequest,
  PlaceSuggestion
} from '../api/GoogleLocationAPI.model'
import { RequestFailure, toRequestFailure } from '../utils/requestFailure'

export interface PlaceSearchState {
  /** What is in the field, as typed. */
  text: string
  status: 'idle' | 'loading' | 'done' | 'failed'
  suggestions: PlaceSuggestion[]
  /** False once the owner asked to look beyond restaurants. */
  restrictToFood: boolean
  failure: RequestFailure | null
}

export const initialPlaceSearchState: PlaceSearchState = {
  text: '',
  status: 'idle',
  suggestions: [],
  restrictToFood: true,
  failure: null
}

/*
  What is sent for a text. The backend refuses control characters with a 400,
  and a pasted tab is one, so they become spaces here rather than an error.
*/
const toQuery = (text: string) => text.replace(/\p{Cc}/gu, ' ').trim()

interface PlaceSearchOptions {
  autocomplete: (
    request: AutocompleteRequest,
    signal: AbortSignal
  ) => Promise<PlaceSuggestion[]>
  onChange: (state: PlaceSearchState) => void
  newSessionToken?: () => string
  now?: () => number
}

/**
 * One search box's worth of autocomplete, without React, so its timing can
 * be tested with fake timers.
 *
 * Google bills autocomplete per session: every request from the first
 * keystroke to the selection carries one token, and the selection closes it.
 * So the token is made at the first character, kept for every request and
 * for the selection, and dropped when a place is picked (`endSession`), the
 * field is emptied or the view is left (`reset`).
 *
 * Requests are only ever sent by typing or by asking for a wider search:
 * nothing is retried on its own, and a 429 that says how long to wait holds
 * back the next request until then.
 */
export function createPlaceSearch(options: PlaceSearchOptions) {
  const {
    autocomplete,
    onChange,
    newSessionToken = () => crypto.randomUUID(),
    now = Date.now
  } = options

  let state = initialPlaceSearchState
  let sessionToken: string | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: AbortController | null = null
  // Every answer is checked against this, so one that arrives after a newer
  // request was sent is dropped even if its request could not be aborted.
  let latestRequest = 0
  // The last query sent and its scope, so a trailing space sends nothing.
  let lastSent: string | null = null
  let blockedUntil = 0

  const update = (patch: Partial<PlaceSearchState>) => {
    state = { ...state, ...patch }
    onChange(state)
  }

  const cancelPending = () => {
    if (timer) clearTimeout(timer)
    timer = null
    inFlight?.abort()
    inFlight = null
    latestRequest += 1
    lastSent = null
  }

  // A rate limit outlives the text it was hit with: it is about the venue.
  const lingeringFailure = () =>
    state.failure?.kind === 'rate-limited' && now() < blockedUntil
      ? state.failure
      : null

  const send = (query: string, restrictToFood: boolean) => {
    if (!sessionToken || now() < blockedUntil) return
    const key = `${restrictToFood}|${query}`
    if (key === lastSent) return
    lastSent = key

    inFlight?.abort()
    const controller = new AbortController()
    inFlight = controller
    latestRequest += 1
    const request = latestRequest
    update({ status: 'loading', failure: null, restrictToFood })

    // A client that throws before it sends (no venue chosen yet, say) fails
    // the search like any other error, instead of leaving it loading.
    let reply: Promise<PlaceSuggestion[]>
    try {
      reply = autocomplete(
        { input: query, sessionToken, restrictToFood },
        controller.signal
      )
    } catch (error) {
      reply = Promise.reject(error)
    }

    reply.then(
      suggestions => {
        if (request !== latestRequest) return
        inFlight = null
        update({ status: 'done', suggestions })
      },
      error => {
        if (request !== latestRequest || controller.signal.aborted) return
        inFlight = null
        // The same text may be tried again by hand, once allowed.
        lastSent = null
        const failure = toRequestFailure(error, now())
        if (failure.kind === 'rate-limited' && failure.retryAfterSeconds) {
          blockedUntil = now() + failure.retryAfterSeconds * 1000
        }
        update({ status: 'failed', suggestions: [], failure })
      }
    )
  }

  const reset = () => {
    cancelPending()
    sessionToken = null
    state = { ...initialPlaceSearchState, failure: lingeringFailure() }
  }

  return {
    getState: () => state,
    getSessionToken: () => sessionToken,

    input(raw: string) {
      const text = raw.slice(0, MAX_QUERY_LENGTH)
      const query = toQuery(text)

      if (query === '') {
        reset()
        update({ text })
        return
      }

      if (!sessionToken) sessionToken = newSessionToken()

      if (query.length < MIN_QUERY_LENGTH) {
        cancelPending()
        update({
          text,
          status: 'idle',
          suggestions: [],
          failure: lingeringFailure()
        })
        return
      }

      if (timer) clearTimeout(timer)
      update({ text })
      timer = setTimeout(() => {
        timer = null
        send(query, state.restrictToFood)
      }, SEARCH_DEBOUNCE_MS)
    },

    /** The same text again, beyond restaurants, cafés and bars. */
    searchWider() {
      const query = toQuery(state.text)
      if (query.length < MIN_QUERY_LENGTH) return
      if (timer) clearTimeout(timer)
      timer = null
      send(query, false)
    },

    /**
     * Hands over the token for the selection, which closes the session. The
     * text stays in the field while the pick is saved; the next keystroke
     * starts a new session.
     */
    endSession(): string | null {
      const token = sessionToken
      const { text } = state
      reset()
      update({ text })
      return token
    },

    /** The field was left: nothing more is sent and the token is dropped. */
    reset
  }
}

export type PlaceSearch = ReturnType<typeof createPlaceSearch>
